/**
 * VariationsEditor — Phase 19-A F1
 * Inline editor voor product-variaties (Maat S, Maat L, etc.) binnen het
 * Phase 4A admin product-formulier. Hergebruikt bestaande endpoints:
 *   GET    /api/admin/webshop/products/{pid}/variations
 *   POST   /api/admin/webshop/products/{pid}/variations
 *   PUT    /api/admin/webshop/variations/{vid}
 *   DELETE /api/admin/webshop/variations/{vid}
 *   POST   /api/admin/webshop/variations/{vid}/stock
 *
 * R4 compliance: alle sub-componenten zijn TOP-LEVEL gedefinieerd zodat
 * React identiteit per render stabiel blijft → geen unmount, geen
 * focus-loss tijdens typen.
 */
import React, { useCallback, useEffect, useState } from "react";
import { adminApi, formatApiError, useAuth } from "../../../context/AuthContext";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import { Switch } from "../../ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../ui/select";
import { toast } from "sonner";
import {
  Plus, Save, Trash2, Power, Layers, Boxes, History, ArrowUp, ArrowDown,
  Image as ImageIcon,
} from "lucide-react";
import ImageField from "../shared/ImageField";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function variantImageSrc(url) {
  if (!url) return "";
  if (/^https?:\/\//i.test(url)) return url;
  return `${BACKEND_URL}${url.startsWith("/") ? "" : "/"}${url}`;
}

// ---------- constants ----------
const STOCK_STATUSES = [
  { v: "in_stock", l: "Op voorraad" },
  { v: "out_of_stock", l: "Niet op voorraad" },
  { v: "backorder", l: "Backorder" },
  { v: "n/a", l: "n.v.t." },
];

const ATTRIBUTE_TYPES = [
  { v: "size", l: "Maat" },
  { v: "liter", l: "Liter / volume" },
  { v: "weight", l: "Gewicht" },
  { v: "destination", l: "Bestemming" },
  { v: "service_type", l: "Service-type" },
  { v: "shipping_method", l: "Verzendmethode" },
  { v: "custom", l: "Aangepast" },
];

const STOCK_REASONS = [
  { v: "manual_correction", l: "Handmatige correctie" },
  { v: "new_stock", l: "Nieuwe voorraad" },
  { v: "damaged", l: "Beschadigd" },
  { v: "sold", l: "Verkocht" },
  { v: "return", l: "Retour" },
];

const STOCK_STATUS_BADGE = {
  in_stock: "bg-green-100 text-green-700",
  out_of_stock: "bg-red-100 text-red-700",
  backorder: "bg-amber-100 text-amber-700",
  "n/a": "bg-gray-100 text-gray-600",
};

const STRIPE_BADGE = {
  synced: "bg-green-100 text-green-700",
  not_synced: "bg-gray-100 text-gray-700",
  pending: "bg-amber-100 text-amber-700",
  error: "bg-red-100 text-red-700",
  "n/a": "bg-gray-100 text-gray-500",
};

const EMPTY_VARIANT = {
  name: "",
  attribute_type: "size",
  attribute_value: "",
  sku: "",
  regular_price: "",
  sale_price: "",
  vat_percentage: 21.0,
  stock_qty: 0,
  stock_status: "in_stock",
  weight: "",
  length: "",
  width: "",
  height: "",
  image_url: "",
  active: true,
};

// ---------- helpers ----------
function numericize(v) {
  if (v === "" || v === null || v === undefined) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function buildPayload(form) {
  return {
    name: form.name?.trim() || "",
    attribute_type: form.attribute_type || "custom",
    attribute_value: form.attribute_value?.trim() || null,
    sku: form.sku?.trim() || null,
    regular_price: numericize(form.regular_price),
    sale_price: numericize(form.sale_price),
    vat_percentage: numericize(form.vat_percentage) ?? 21.0,
    stock_qty: numericize(form.stock_qty) ?? 0,
    stock_status: form.stock_status || "in_stock",
    weight: numericize(form.weight),
    length: numericize(form.length),
    width: numericize(form.width),
    height: numericize(form.height),
    image_url: form.image_url?.trim() || null,
    active: !!form.active,
  };
}

// ---------- TOP-LEVEL SUB-COMPONENTS (R4) ----------

function VariantFormFields({ form, setForm, canEditPrice, canEditStock }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
      <div>
        <Label className="text-xs uppercase font-bold">Naam</Label>
        <Input
          value={form.name || ""}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
          placeholder="Bijv. Maat S"
          className="rounded-none mt-1"
          data-testid="variant-form-name"
        />
      </div>
      <div>
        <Label className="text-xs uppercase font-bold">Attribuut-type</Label>
        <Select
          value={form.attribute_type || "custom"}
          onValueChange={(v) => setForm({ ...form, attribute_type: v })}
        >
          <SelectTrigger className="rounded-none mt-1" data-testid="variant-form-attribute-type">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {ATTRIBUTE_TYPES.map((a) => (
              <SelectItem key={a.v} value={a.v}>{a.l}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label className="text-xs uppercase font-bold">Attribuut-waarde</Label>
        <Input
          value={form.attribute_value || ""}
          onChange={(e) => setForm({ ...form, attribute_value: e.target.value })}
          placeholder="Bijv. S"
          className="rounded-none mt-1"
          data-testid="variant-form-attribute-value"
        />
      </div>
      <div>
        <Label className="text-xs uppercase font-bold">SKU</Label>
        <Input
          value={form.sku || ""}
          onChange={(e) => setForm({ ...form, sku: e.target.value })}
          placeholder="Bijv. SHIRT-S"
          className="rounded-none mt-1"
          data-testid="variant-form-sku"
        />
      </div>
      <div>
        <Label className="text-xs uppercase font-bold">Normale prijs (€)</Label>
        <Input
          type="number"
          step="0.01"
          value={form.regular_price ?? ""}
          onChange={(e) => setForm({ ...form, regular_price: e.target.value })}
          disabled={!canEditPrice}
          className="rounded-none mt-1"
          data-testid="variant-form-regular-price"
        />
      </div>
      <div>
        <Label className="text-xs uppercase font-bold">Actieprijs (€)</Label>
        <Input
          type="number"
          step="0.01"
          value={form.sale_price ?? ""}
          onChange={(e) => setForm({ ...form, sale_price: e.target.value })}
          disabled={!canEditPrice}
          className="rounded-none mt-1"
          data-testid="variant-form-sale-price"
        />
      </div>
      <div>
        <Label className="text-xs uppercase font-bold">BTW %</Label>
        <Input
          type="number"
          step="0.01"
          value={form.vat_percentage ?? 21}
          onChange={(e) => setForm({ ...form, vat_percentage: e.target.value })}
          className="rounded-none mt-1"
          data-testid="variant-form-vat"
        />
      </div>
      <div>
        <Label className="text-xs uppercase font-bold">Voorraad</Label>
        <Input
          type="number"
          value={form.stock_qty ?? 0}
          onChange={(e) => setForm({ ...form, stock_qty: e.target.value })}
          disabled={!canEditStock}
          className="rounded-none mt-1"
          data-testid="variant-form-stock-qty"
        />
      </div>
      <div>
        <Label className="text-xs uppercase font-bold">Voorraadstatus</Label>
        <Select
          value={form.stock_status || "in_stock"}
          onValueChange={(v) => setForm({ ...form, stock_status: v })}
        >
          <SelectTrigger className="rounded-none mt-1" data-testid="variant-form-stock-status">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {STOCK_STATUSES.map((s) => (
              <SelectItem key={s.v} value={s.v}>{s.l}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="sm:col-span-2 lg:col-span-3">
        <ImageField
          value={form.image_url || ""}
          onChange={(url) => setForm({ ...form, image_url: url })}
          label="Variant-afbeelding (optioneel — anders wordt productfoto gebruikt)"
          testIdPrefix="variant-form-image"
          compact
        />
      </div>
      <div className="flex items-end gap-3">
        <Switch
          checked={!!form.active}
          onCheckedChange={(v) => setForm({ ...form, active: v })}
          data-testid="variant-form-active"
        />
        <Label className="text-xs uppercase font-bold">Actief</Label>
      </div>
    </div>
  );
}

function StockAdjustDialog({ variant, onClose, onApplied }) {
  const [qty, setQty] = useState(variant?.stock_qty ?? 0);
  const [reason, setReason] = useState("manual_correction");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const onSubmit = async () => {
    const n = Number(qty);
    if (!Number.isFinite(n) || n < 0) {
      toast.error("Voorraad moet een positief geheel getal zijn");
      return;
    }
    setBusy(true);
    try {
      await adminApi.post(`/admin/webshop/variations/${variant.id}/stock`, {
        new_qty: Math.round(n),
        reason,
        note: note.trim() || null,
      });
      toast.success(`Voorraad bijgewerkt: ${variant.name}`);
      onApplied();
      onClose();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="variant-stock-dialog"
    >
      <div
        className="bg-white max-w-md w-full p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="font-chivo font-black text-lg mb-1">Voorraad bijwerken</h3>
        <p className="text-xs text-gray-500 mb-4">
          Variant: <strong>{variant.name}</strong> · huidig {variant.stock_qty ?? 0}
        </p>
        <div className="space-y-3">
          <div>
            <Label className="text-xs uppercase font-bold">Nieuwe voorraad</Label>
            <Input
              type="number"
              min="0"
              value={qty}
              onChange={(e) => setQty(e.target.value)}
              className="rounded-none mt-1"
              data-testid="variant-stock-dialog-qty"
            />
          </div>
          <div>
            <Label className="text-xs uppercase font-bold">Reden (verplicht)</Label>
            <Select value={reason} onValueChange={setReason}>
              <SelectTrigger className="rounded-none mt-1" data-testid="variant-stock-dialog-reason">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {STOCK_REASONS.map((r) => (
                  <SelectItem key={r.v} value={r.v}>{r.l}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs uppercase font-bold">Notitie (optioneel)</Label>
            <Input
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Bijv. nieuwe pallet ontvangen"
              className="rounded-none mt-1"
              data-testid="variant-stock-dialog-note"
            />
          </div>
        </div>
        <div className="mt-5 flex gap-2 justify-end">
          <Button variant="outline" className="rounded-none" onClick={onClose}>
            Annuleren
          </Button>
          <Button
            onClick={onSubmit}
            disabled={busy}
            className="bg-[#0055FF] hover:bg-[#0A192F] text-white rounded-none font-chivo font-bold uppercase text-xs"
            data-testid="variant-stock-dialog-save"
          >
            {busy ? "Bezig…" : "Opslaan"}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ---------- MAIN ----------

export default function VariationsEditor({ productId, productName }) {
  const { hasPermission, isAdminOrOwner } = useAuth();
  const canManage = hasPermission("webshop:manage_variations");
  const canManageStock = hasPermission("webshop:manage_stock");
  const canManagePrice = hasPermission("webshop:manage_prices");
  const canDelete = isAdminOrOwner();

  const [variants, setVariants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ ...EMPTY_VARIANT });
  const [saving, setSaving] = useState(false);
  const [stockDialogVariant, setStockDialogVariant] = useState(null);

  const load = useCallback(async () => {
    if (!productId) return;
    setLoading(true);
    try {
      const r = await adminApi.get(`/admin/webshop/products/${productId}/variations`);
      setVariants(r.data || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [productId]);

  useEffect(() => { load(); }, [load]);

  const startCreate = () => {
    setEditingId(null);
    setForm({ ...EMPTY_VARIANT });
    setCreating(true);
  };

  const startEdit = (v) => {
    setCreating(false);
    setEditingId(v.id);
    setForm({
      name: v.name || "",
      attribute_type: v.attribute_type || "custom",
      attribute_value: v.attribute_value || "",
      sku: v.sku || "",
      regular_price: v.regular_price ?? "",
      sale_price: v.sale_price ?? "",
      vat_percentage: v.vat_percentage ?? 21.0,
      stock_qty: v.stock_qty ?? 0,
      stock_status: v.stock_status || "in_stock",
      weight: v.weight ?? "",
      length: v.length ?? "",
      width: v.width ?? "",
      height: v.height ?? "",
      image_url: v.image_url || "",
      active: v.active !== false,
    });
  };

  const cancelForm = () => {
    setCreating(false);
    setEditingId(null);
    setForm({ ...EMPTY_VARIANT });
  };

  const onSave = async () => {
    if (!form.name?.trim()) {
      toast.error("Naam is verplicht");
      return;
    }
    const payload = buildPayload(form);
    setSaving(true);
    try {
      if (editingId) {
        await adminApi.put(`/admin/webshop/variations/${editingId}`, payload);
        toast.success("Variant bijgewerkt");
      } else {
        await adminApi.post(`/admin/webshop/products/${productId}/variations`, payload);
        toast.success("Variant aangemaakt");
      }
      cancelForm();
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const onToggleActive = async (v) => {
    try {
      const payload = buildPayload({ ...v, active: !v.active });
      await adminApi.put(`/admin/webshop/variations/${v.id}`, payload);
      toast.success(v.active ? "Variant gedeactiveerd" : "Variant geactiveerd");
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const onDelete = async (v) => {
    if (!canDelete) {
      toast.error("Alleen Owner/Admin kan verwijderen");
      return;
    }
    if (!window.confirm(`Variant "${v.name}" definitief verwijderen?\nDit kan niet ongedaan worden gemaakt.`)) return;
    try {
      await adminApi.delete(`/admin/webshop/variations/${v.id}`);
      toast.success("Variant verwijderd");
      if (editingId === v.id) cancelForm();
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  // F4 — Reorder variant up/down. Backend swaps sort_order with neighbour.
  const onMove = async (v, direction) => {
    if (!canManage) return;
    try {
      await adminApi.post(`/admin/webshop/variations/${v.id}/move`, { direction });
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  // Compute aggregate (used by F2 too)
  const totals = variants.reduce(
    (acc, v) => {
      acc.count += 1;
      if (v.active !== false) acc.activeCount += 1;
      acc.totalStock += Number(v.stock_qty || 0);
      if (v.stripe_sync_status === "synced") acc.synced += 1;
      else if (v.stripe_sync_status === "error") acc.errored += 1;
      return acc;
    },
    { count: 0, activeCount: 0, totalStock: 0, synced: 0, errored: 0 }
  );

  if (!productId) {
    return (
      <div className="border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800" data-testid="variations-editor-needs-save">
        Sla het product eerst op voordat u variaties toevoegt.
      </div>
    );
  }

  return (
    <div
      className="border border-blue-200 bg-blue-50/30 p-4 mt-4"
      data-testid={`variations-editor-${productId}`}
    >
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Layers className="w-5 h-5 text-[#0055FF]" />
          <h4 className="font-chivo font-black text-base text-[#0A192F]">
            Variaties beheren
          </h4>
        </div>
        {canManage && !creating && !editingId && (
          <Button
            onClick={startCreate}
            className="rounded-none bg-[#0055FF] hover:bg-[#0A192F] text-white font-chivo font-bold uppercase text-xs"
            data-testid="variant-new-button"
          >
            <Plus className="w-4 h-4 mr-1" /> Nieuwe variant
          </Button>
        )}
      </div>

      {/* Summary banner */}
      <div
        className="bg-white border border-gray-200 p-3 mb-4 grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs"
        data-testid="variations-summary"
      >
        <div>
          <div className="uppercase text-gray-500 font-bold">Aantal varianten</div>
          <div className="font-chivo font-black text-lg" data-testid="variations-summary-count">
            {totals.count}
            <span className="text-xs text-gray-500 font-normal ml-1">
              ({totals.activeCount} actief)
            </span>
          </div>
        </div>
        <div>
          <div className="uppercase text-gray-500 font-bold">Totale voorraad</div>
          <div className="font-chivo font-black text-lg" data-testid="variations-summary-stock">
            <Boxes className="w-4 h-4 inline mr-1 -mt-0.5" />
            {totals.totalStock}
          </div>
        </div>
        <div>
          <div className="uppercase text-gray-500 font-bold">Stripe gesync.</div>
          <div className={`font-chivo font-black text-lg ${totals.synced === totals.activeCount && totals.synced > 0 ? "text-green-700" : "text-gray-700"}`}>
            {totals.synced} / {totals.activeCount}
          </div>
        </div>
        <div>
          <div className="uppercase text-gray-500 font-bold">Met fout</div>
          <div className={`font-chivo font-black text-lg ${totals.errored > 0 ? "text-red-700" : "text-gray-500"}`}>
            {totals.errored}
          </div>
        </div>
      </div>

      {/* Edit/create form (top, R4-stable) */}
      {(creating || editingId) && (
        <div className="bg-white border border-blue-300 p-4 mb-4" data-testid="variant-form-panel">
          <h5 className="font-chivo font-bold text-sm mb-3">
            {editingId ? "Variant bewerken" : "Nieuwe variant"}
          </h5>
          <VariantFormFields
            form={form}
            setForm={setForm}
            canEditPrice={canManagePrice || !editingId}
            canEditStock={canManageStock || !editingId}
          />
          <div className="mt-4 flex items-center gap-2">
            <Button
              onClick={onSave}
              disabled={saving}
              className="rounded-none bg-[#0055FF] hover:bg-[#0A192F] text-white font-chivo font-bold uppercase text-xs"
              data-testid="variant-form-save"
            >
              <Save className="w-4 h-4 mr-1" /> {saving ? "Bezig…" : "Opslaan"}
            </Button>
            <Button
              variant="outline"
              onClick={cancelForm}
              className="rounded-none"
              data-testid="variant-form-cancel"
            >
              Annuleren
            </Button>
          </div>
        </div>
      )}

      {/* List */}
      <div className="bg-white border border-gray-200 overflow-x-auto">
        {loading ? (
          <div className="p-6 text-gray-500 text-sm">Laden…</div>
        ) : variants.length === 0 ? (
          <div className="p-6 text-gray-500 text-sm" data-testid="variations-empty">
            Nog geen varianten. Klik op <strong>+ Nieuwe variant</strong> om bv.
            <em> Maat S</em> en <em>Maat L</em> aan te maken.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wide text-gray-500 border-b">
                <th className="py-3 px-3 font-bold">Foto</th>
                <th className="py-3 px-3 font-bold">Naam</th>
                <th className="py-3 px-3 font-bold">SKU</th>
                <th className="py-3 px-3 font-bold">Prijs</th>
                <th className="py-3 px-3 font-bold">Voorraad</th>
                <th className="py-3 px-3 font-bold">Stripe</th>
                <th className="py-3 px-3 font-bold">Actief</th>
                <th className="py-3 px-3 font-bold">Acties</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {variants.map((v, idx) => (
                <tr key={v.id} data-testid={`variant-row-${v.id}`}>
                  <td className="py-2 px-3">
                    {v.image_url ? (
                      <img
                        src={variantImageSrc(v.image_url)}
                        alt={v.name}
                        className="w-10 h-10 object-cover border border-gray-200"
                        onError={(e) => { e.currentTarget.style.display = "none"; }}
                        data-testid={`variant-thumb-${v.id}`}
                      />
                    ) : (
                      <div
                        className="w-10 h-10 bg-gray-50 border border-gray-200 flex items-center justify-center text-gray-300"
                        data-testid={`variant-thumb-fallback-${v.id}`}
                        title="Geen variant-foto — productfoto wordt gebruikt"
                      >
                        <ImageIcon className="w-4 h-4" />
                      </div>
                    )}
                  </td>
                  <td className="py-3 px-3 font-bold">
                    {v.name}
                    {v.attribute_value && (
                      <span className="ml-2 text-xs text-gray-500 font-normal">
                        ({v.attribute_value})
                      </span>
                    )}
                  </td>
                  <td className="py-3 px-3 font-mono text-xs">{v.sku || "—"}</td>
                  <td className="py-3 px-3">
                    € {Number(v.regular_price ?? 0).toFixed(2)}
                    {v.sale_price ? (
                      <span className="ml-1 text-xs text-red-600 font-bold">
                        (actie € {Number(v.sale_price).toFixed(2)})
                      </span>
                    ) : null}
                  </td>
                  <td className="py-3 px-3">
                    <span
                      className={`inline-block px-2 py-0.5 text-xs ${STOCK_STATUS_BADGE[v.stock_status] || STOCK_STATUS_BADGE["n/a"]}`}
                    >
                      {v.stock_qty ?? 0}
                    </span>
                  </td>
                  <td className="py-3 px-3 text-xs">
                    <span
                      className={`inline-block px-2 py-0.5 ${STRIPE_BADGE[v.stripe_sync_status || "n/a"] || STRIPE_BADGE["n/a"]}`}
                      data-testid={`variant-stripe-status-${v.id}`}
                      title={v.stripe_sync_error || ""}
                    >
                      {v.stripe_sync_status || "n/a"}
                    </span>
                  </td>
                  <td className="py-3 px-3">
                    {v.active !== false ? (
                      <span className="text-xs text-green-700 font-bold">Ja</span>
                    ) : (
                      <span className="text-xs text-gray-400">Nee</span>
                    )}
                  </td>
                  <td className="py-3 px-3 whitespace-nowrap">
                    {canManage && (
                      <span className="inline-flex items-center mr-2">
                        <button
                          onClick={() => onMove(v, "up")}
                          disabled={idx === 0}
                          className="text-gray-500 hover:text-[#0055FF] disabled:text-gray-300 disabled:cursor-not-allowed"
                          title="Omhoog"
                          data-testid={`variant-move-up-${v.id}`}
                        >
                          <ArrowUp className="w-3.5 h-3.5" />
                        </button>
                        <button
                          onClick={() => onMove(v, "down")}
                          disabled={idx === variants.length - 1}
                          className="text-gray-500 hover:text-[#0055FF] disabled:text-gray-300 disabled:cursor-not-allowed ml-0.5"
                          title="Omlaag"
                          data-testid={`variant-move-down-${v.id}`}
                        >
                          <ArrowDown className="w-3.5 h-3.5" />
                        </button>
                      </span>
                    )}
                    {canManage && (
                      <button
                        onClick={() => startEdit(v)}
                        className="text-xs text-[#0055FF] hover:underline mr-2"
                        data-testid={`variant-edit-${v.id}`}
                      >
                        Bewerken
                      </button>
                    )}
                    {canManageStock && (
                      <button
                        onClick={() => setStockDialogVariant(v)}
                        className="text-xs text-gray-700 hover:text-[#0055FF] mr-2"
                        title="Voorraad aanpassen"
                        data-testid={`variant-stock-${v.id}`}
                      >
                        <History className="w-3 h-3 inline mr-0.5" />
                        Voorraad
                      </button>
                    )}
                    {canManage && (
                      <button
                        onClick={() => onToggleActive(v)}
                        className="text-xs text-gray-600 hover:text-[#0055FF] mr-2"
                        title={v.active !== false ? "Deactiveren" : "Activeren"}
                        data-testid={`variant-toggle-${v.id}`}
                      >
                        <Power className="w-3 h-3 inline" />
                      </button>
                    )}
                    {canDelete && (
                      <button
                        onClick={() => onDelete(v)}
                        className="text-xs text-red-500 hover:underline"
                        data-testid={`variant-delete-${v.id}`}
                      >
                        <Trash2 className="w-3 h-3 inline" />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {!canManage && (
        <p className="text-xs text-gray-500 mt-3" data-testid="variations-readonly-note">
          Alleen Owner/Admin/Webshop Manager kunnen varianten aanmaken of bewerken.
        </p>
      )}

      {stockDialogVariant && (
        <StockAdjustDialog
          variant={stockDialogVariant}
          onClose={() => setStockDialogVariant(null)}
          onApplied={load}
        />
      )}
    </div>
  );
}

// Export the aggregate helper so the products list (F2) can reuse it
export function variantsAggregate(variants) {
  if (!Array.isArray(variants)) return { count: 0, activeCount: 0, totalStock: 0 };
  return variants.reduce(
    (acc, v) => {
      acc.count += 1;
      if (v.active !== false) acc.activeCount += 1;
      acc.totalStock += Number(v.stock_qty || 0);
      return acc;
    },
    { count: 0, activeCount: 0, totalStock: 0 }
  );
}
